import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { HiLockClosed, HiCheck, HiX, HiEye, HiEyeOff } from 'react-icons/hi'
import { useAuth } from '../contexts/AuthContext'
import logger from '../utils/logger'
import usePageTitle from '../hooks/usePageTitle'

/**
 * Password strength indicator component
 */
function PasswordStrengthIndicator({ password }) {
    const getStrength = () => {
        if (!password) return { score: 0, label: '', color: '' }

        let score = 0

        // Length checks
        if (password.length >= 8) score += 1
        if (password.length >= 12) score += 1

        // Character variety checks
        if (/[a-z]/.test(password)) score += 1
        if (/[A-Z]/.test(password)) score += 1
        if (/[0-9]/.test(password)) score += 1
        if (/[^a-zA-Z0-9]/.test(password)) score += 1

        // Normalize to 0-4 scale
        const normalizedScore = Math.min(4, Math.floor(score * 4 / 6))

        const labels = ['Weak', 'Fair', 'Good', 'Strong']
        const colors = ['var(--error-color)', 'var(--warning-color)', 'var(--accent-secondary)', 'var(--success-color)']

        return {
            score: normalizedScore,
            label: labels[normalizedScore - 1] || '',
            color: colors[normalizedScore - 1] || ''
        }
    }

    const strength = getStrength()

    if (!password) return null

    return (
        <div style={{ marginTop: '0.5rem' }}>
            <div style={{
                display: 'flex',
                gap: '4px',
                marginBottom: '4px'
            }}>
                {[1, 2, 3, 4].map(level => (
                    <div
                        key={level}
                        style={{
                            flex: 1,
                            height: '4px',
                            borderRadius: '2px',
                            backgroundColor: level <= strength.score ? strength.color : 'var(--bg-tertiary)',
                            transition: 'background-color 0.3s'
                        }}
                    />
                ))}
            </div>
            <span style={{ fontSize: '0.75rem', color: strength.color }}>
                {strength.label}
            </span>
        </div>
    )
}

/**
 * ResetPassword component
 * Handles the password reset flow after user clicks the reset link in their email
 */
function ResetPassword() {
    usePageTitle('Reset Password')
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { updatePassword, loading } = useAuth()

    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Check for access token in URL (Supabase recovery link)
    useEffect(() => {
        // Supabase handles the token extraction automatically via onAuthStateChange
        // We just need to check if we're in a valid password recovery state
        const type = searchParams.get('type')
        if (type && type !== 'recovery') {
            logger.warn('Invalid reset password link type', { type })
        }
    }, [searchParams])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')

        // Validation
        if (password.length < 8) {
            setError('Password must be at least 8 characters long')
            return
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match')
            return
        }

        setIsSubmitting(true)

        try {
            const result = await updatePassword(password)

            if (result.error) {
                setError(result.error.message || 'Failed to reset password')
                return
            }

            setSuccess(true)
            logger.info('Password reset successful')

            // Redirect to home after a short delay
            setTimeout(() => {
                navigate('/')
            }, 3000)

        } catch (err) {
            logger.error('Password reset error', { error: err.message })
            setError('An unexpected error occurred. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Success state
    if (success) {
        return (
            <div style={{ padding: '2rem 0' }} className="fade-in">
                <div className="card fade-in-up" style={{
                    maxWidth: '400px',
                    margin: '0 auto',
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--success)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1.5rem'
                    }}>
                        <HiCheck style={{ fontSize: '2rem', color: 'white' }} />
                    </div>
                    <h2 style={{ margin: '0 0 1rem' }}>Password Reset Successful!</h2>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                        Your password has been updated. Redirecting you to the home page...
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/')}
                        style={{ width: '100%' }}
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div style={{ padding: '2rem 0' }} className="fade-in">
            <div className="card fade-in-up" style={{
                maxWidth: '400px',
                margin: '0 auto'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.5rem',
                    justifyContent: 'center'
                }}>
                    <HiLockClosed style={{ fontSize: '1.75rem', color: 'var(--accent-primary)' }} />
                    <h2 style={{ margin: 0 }}>Reset Password</h2>
                </div>
                <p style={{
                    marginBottom: '1.5rem',
                    color: 'var(--text-secondary)',
                    textAlign: 'center',
                    lineHeight: '1.6'
                }}>
                    Enter your new password below.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="password" className="form-label">
                            New Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                className="form-input"
                                placeholder="Enter new password..."
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                style={{ paddingRight: '3rem' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)',
                                    padding: '0.25rem'
                                }}
                            >
                                {showPassword ? <HiEyeOff /> : <HiEye />}
                            </button>
                        </div>
                        <PasswordStrengthIndicator password={password} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword" className="form-label">
                            Confirm Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                className="form-input"
                                placeholder="Confirm new password..."
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={8}
                                style={{ paddingRight: '3rem' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)',
                                    padding: '0.25rem'
                                }}
                            >
                                {showConfirmPassword ? <HiEyeOff /> : <HiEye />}
                            </button>
                        </div>
                        {password && confirmPassword && password !== confirmPassword && (
                            <div style={{
                                fontSize: '0.75rem',
                                color: 'var(--error)',
                                marginTop: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                            }}>
                                <HiX /> Passwords do not match
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="form-error" style={{
                            animation: 'slideIn 0.3s ease-out',
                            marginBottom: '1rem'
                        }}>
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className={`btn btn-primary ${isSubmitting || loading ? 'btn-loading' : ''}`}
                        style={{ width: '100%' }}
                        disabled={isSubmitting || loading || !password || password !== confirmPassword}
                    >
                        {isSubmitting || loading ? 'Updating...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        </div>
    )
}

export default ResetPassword
